import type { ErrorEvent } from "@sentry/react";

// Events `beforeSend` drops because nothing in our code can act on them. Pure, so it is tested
// without initializing Sentry (src/utils/sentry/shouldFilterKnownNoise.test.ts).

// Cancelled requests (route changes, component unmounts, page teardown). Axios rejects with
// "Request aborted" from the XHR `onabort`; when nobody awaits that promise the global handler
// reports it with the "Unhandled Promise Rejection: " prefix (REFABRIC-FRONTEND-6D).
const CANCELLED_VALUES = new Set(["Request aborted", "Unhandled Promise Rejection: Request aborted", "canceled"]);

// Firefox throws a bare XPCOM `NS_ERROR_*` ("No error message") when a profile's localStorage is
// broken. Hotjar's script reads localStorage on load, so those reach us from script.hotjar.com
// (REFABRIC-FRONTEND-5B / -50). Matched on the frame that threw, so an NS_ERROR from our own
// bundle still reports.
const isHotjarNativeError = (type: string | undefined, frames: { filename?: string }[]): boolean =>
  !!type?.startsWith("NS_ERROR_") && !!frames[frames.length - 1]?.filename?.startsWith("https://script.hotjar.com/");

export const shouldFilterKnownNoise = (event: ErrorEvent): boolean => {
  const values = event.exception?.values ?? [];

  if (values.some((v) => CANCELLED_VALUES.has(v.value ?? "") || v.type === "CanceledError")) {
    return true;
  }

  return values.some((v) => isHotjarNativeError(v.type, v.stacktrace?.frames ?? []));
};
